import { tz } from '@date-fns/tz';
import { format, parseISO } from 'date-fns';

import { getDateFnsLocale } from './dateFnsLocale';

/**
 * Time-of-day in the caller's locale style, optionally with a short timezone
 * abbreviation.
 *
 * - `en-US` → 12-hour with AM/PM (`"3:14 PM"`).
 * - Every other locale → 24-hour (`"15:14"`).
 * - With `showTimezone: true` and a `timezone` argument, appends the short
 *   abbreviation resolved by `Intl.DateTimeFormat` (`"17:14 CEST"`, `"8:14 AM PDT"`).
 *
 * The locale mapping lives in `getDateFnsLocale` — this function is the single
 * place that decides how a bare clock reading looks to the user, so a future
 * policy change flips every call site through one edit here.
 *
 * @param {string | Date | null | undefined} datetime UTC ISO string or a
 *   `Date` instance. Falsy / invalid inputs return `""` so JSX can render
 *   the result unconditionally.
 * @param {{ timezone?: string, locale?: string, showTimezone?: boolean }} [options]
 *   - `timezone` — IANA zone (e.g. `"Europe/Berlin"`). Defaults to the browser's
 *     zone when omitted.
 *   - `locale` — BCP-47 tag (e.g. `"en-US"`). Defaults to `navigator.language`.
 *   - `showTimezone` — when `true`, append the short zone abbreviation. Requires
 *     `timezone`; without one, the bare time is returned unchanged.
 * @returns {string} the rendered time, or `""` for a bad input.
 */
export const formatTime = (datetime, { timezone, locale, showTimezone = false } = {}) => {
	if (!datetime) return '';
	const parsed = typeof datetime === 'string' ? parseISO(datetime) : datetime;
	if (Number.isNaN(parsed?.getTime?.())) return '';

	const options = { locale: getDateFnsLocale(locale) };
	if (timezone) options.in = tz(timezone);

	const time = format(parsed, 'p', options);

	if (!showTimezone || !timezone) return time;

	const abbr = getZoneAbbreviation(parsed, timezone, locale);
	return abbr ? `${time} ${abbr}` : time;
};

const getZoneAbbreviation = (date, timezone, locale) => {
	const parts = new Intl.DateTimeFormat(locale || undefined, {
		timeZone: timezone,
		timeZoneName: 'short',
	}).formatToParts(date);
	return parts.find((part) => part.type === 'timeZoneName')?.value ?? '';
};
